import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { AdminIcon, type IconName } from "@/components/admin/icon";
import { Popover } from "@/components/admin/popover";
import { getCrumbs } from "@/data/admin-nav";
import { useAuth } from "@/hooks/use-auth";
import {
	libelleRubrique,
	useGlobalSearch,
} from "@/hooks/use-global-search";
import { useNotificationLogs } from "@/hooks/use-notification-logs";
import { usePermissions } from "@/hooks/use-permissions";

interface AdminTopbarProps {
	onMenuToggle?: () => void;
	navOpen?: boolean;
}

const STATUS_META: Record<string, { icn: IconName; label: string }> = {
	sent: { icn: "check", label: "Envoyé" },
	delivered: { icn: "check", label: "Livré" },
	read: { icn: "eye", label: "Lu" },
	failed: { icn: "alert", label: "Échec" },
	inbound: { icn: "chat", label: "Entrant" },
};

function timeAgo(iso: string): string {
	const diff = Date.now() - new Date(iso).getTime();
	const min = Math.floor(diff / 60_000);
	if (min < 1) return "à l'instant";
	if (min < 60) return `il y a ${min} min`;
	const h = Math.floor(min / 60);
	if (h < 24) return `il y a ${h} h`;
	return `il y a ${Math.floor(h / 24)} j`;
}

export function AdminTopbar({ onMenuToggle, navOpen }: AdminTopbarProps = {}) {
	const location = useLocation();
	const navigate = useNavigate();
	const { user, logout } = useAuth();
	const { has, isSuperAdmin } = usePermissions();
	const [root, here] = getCrumbs(location.pathname);
	const searchRef = useRef<HTMLInputElement>(null);
	const { logs, failedCount, isLoading, isError } = useNotificationLogs();

	// ⌘K / Ctrl+K : focus sur la recherche globale.
	useEffect(() => {
		const onKeyDown = (e: KeyboardEvent) => {
			if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
				e.preventDefault();
				searchRef.current?.focus();
			}
		};
		document.addEventListener("keydown", onKeyDown);
		return () => document.removeEventListener("keydown", onKeyDown);
	}, []);

	const [terme, setTerme] = useState("");
	const [panneauOuvert, setPanneauOuvert] = useState(false);
	const [indexActif, setIndexActif] = useState(-1);
	const { hits, isLoading: chercheEnCours, actif } = useGlobalSearch(terme);

	// La barre promettait « commande, client, produit » mais se contentait
	// d'emmener vers la recherche de commandes, sans rien proposer pendant la
	// frappe. Entrée reste un raccourci vers la recherche avancée quand aucune
	// suggestion n'est retenue.
	const submitSearch = () => {
		const q = terme.trim();
		if (!q) return;
		setPanneauOuvert(false);
		navigate(`/commandes/recherche?q=${encodeURIComponent(q)}`);
	};

	const ouvrir = (index: number) => {
		const hit = hits[index];
		if (!hit) return;
		setPanneauOuvert(false);
		setTerme("");
		navigate(hit.path);
	};

	const surTouche = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === "Escape") {
			setPanneauOuvert(false);
			searchRef.current?.blur();
			return;
		}
		if (e.key === "ArrowDown") {
			e.preventDefault();
			setIndexActif((i) => Math.min(i + 1, hits.length - 1));
			return;
		}
		if (e.key === "ArrowUp") {
			e.preventDefault();
			setIndexActif((i) => Math.max(i - 1, -1));
			return;
		}
		if (e.key === "Enter") {
			if (indexActif >= 0) ouvrir(indexActif);
			else submitSearch();
		}
	};

	return (
		<header className="topbar">
			<button
				className="nav-toggle"
				aria-label={navOpen ? "Fermer le menu" : "Ouvrir le menu"}
				aria-expanded={navOpen}
				onClick={onMenuToggle}
			>
				{navOpen ? "✕" : "☰"}
			</button>
			<div className="crumbs">
				<span className="root">{root}</span>
				<span className="sep">
					<AdminIcon n="chevR" s={12} />
				</span>
				<span className="here">{here}</span>
			</div>

			<div className="search-bar">
				<span className="icn">
					<AdminIcon n="search" s={14} />
				</span>
				<input
					ref={searchRef}
					placeholder="Rechercher une commande, un client, un produit…"
					value={terme}
					onChange={(e) => {
						setTerme(e.target.value);
						setPanneauOuvert(true);
						setIndexActif(-1);
					}}
					onFocus={() => setPanneauOuvert(true)}
					// Laisser le clic sur une suggestion se produire avant la fermeture.
					onBlur={() => setTimeout(() => setPanneauOuvert(false), 150)}
					onKeyDown={surTouche}
					role="combobox"
					aria-expanded={panneauOuvert && actif}
					aria-controls="recherche-globale"
					aria-autocomplete="list"
					data-testid="topbar-search"
				/>
				<span className="kbd">⌘K</span>

				{panneauOuvert && actif && (
					<div className="search-panel" id="recherche-globale" role="listbox">
						{chercheEnCours ? (
							<div className="search-panel-empty">Recherche…</div>
						) : hits.length === 0 ? (
							<div className="search-panel-empty">
								Aucun résultat pour « {terme.trim()} »
							</div>
						) : (
							hits.map((hit, i) => {
								const rubriquePrecedente = i > 0 ? hits[i - 1].kind : null;
								return (
									<div key={`${hit.kind}-${hit.id}`}>
										{hit.kind !== rubriquePrecedente && (
											<div className="search-panel-group">
												{libelleRubrique(hit.kind)}
											</div>
										)}
										<button
											type="button"
											role="option"
											aria-selected={i === indexActif}
											className={
												i === indexActif
													? "search-panel-item active"
													: "search-panel-item"
											}
											onMouseEnter={() => setIndexActif(i)}
											onClick={() => ouvrir(i)}
										>
											<span className="search-panel-label">{hit.label}</span>
											{hit.hint && (
												<span className="search-panel-hint">{hit.hint}</span>
											)}
										</button>
									</div>
								);
							})
						)}
					</div>
				)}
			</div>

			<div className="top-actions">
				<Popover
					width={280}
					testId="help-popover"
					trigger={
						<button className="top-btn" title="Aide">
							<AdminIcon n="info" s={16} />
						</button>
					}
				>
					{(close) => (
						<>
							<div className="popover-h">Aide</div>
							<div className="popover-body">
								<div className="popover-kbd-row">
									<span>Recherche globale</span>
									<span className="kbd">⌘K</span>
								</div>
								<div className="popover-kbd-row">
									<span>Fermer un panneau</span>
									<span className="kbd">Échap</span>
								</div>
							</div>
							<div className="popover-sep" />
							<Link
								to="/support"
								className="popover-item"
								onClick={close}
								role="menuitem"
							>
								<AdminIcon n="chat" s={15} />
								<span>Support CSR</span>
							</Link>
							<div className="popover-foot">Akwaba Admin · v1.12.3</div>
						</>
					)}
				</Popover>

				<Popover
					width={340}
					testId="notifications-popover"
					trigger={
						<button className="top-btn" title="Notifications">
							<AdminIcon n="bell" s={16} />
							{failedCount > 0 && (
								<span className="count-mini">{failedCount}</span>
							)}
						</button>
					}
				>
					{(close) => (
						<>
							<div className="popover-h">
								<span>Notifications</span>
								<Link
									to="/notifications"
									onClick={close}
									className="popover-h-link"
								>
									Tout voir
								</Link>
							</div>
							{isLoading && <div className="popover-empty">Chargement…</div>}
							{isError && (
								<div className="popover-empty">Journal indisponible.</div>
							)}
							{!isLoading && !isError && logs.length === 0 && (
								<div className="popover-empty">
									Aucune notification récente.
								</div>
							)}
							{logs.map((l) => {
								const meta = STATUS_META[l.status] ?? {
									icn: "bell" as IconName,
									label: l.status,
								};
								return (
									<div key={l.id} className="popover-notif">
										<span
											className={`popover-notif-icn${l.status === "failed" ? " failed" : ""}`}
										>
											<AdminIcon n={meta.icn} s={14} />
										</span>
										<span className="popover-notif-body">
											<span className="popover-notif-t">
												{meta.label} · {l.channel}
												{l.event ? ` · ${l.event}` : ""}
											</span>
											<span className="popover-notif-s">
												{l.recipient} · {timeAgo(l.createdAt)}
												{l.errorMessage ? ` — ${l.errorMessage}` : ""}
											</span>
										</span>
									</div>
								);
							})}
						</>
					)}
				</Popover>

				<Popover
					width={240}
					testId="settings-popover"
					trigger={
						<button className="top-btn" title="Paramètres">
							<AdminIcon n="gear" s={16} />
						</button>
					}
				>
					{(close) => (
						<>
							<div className="popover-h">
								<span>{user?.fullName ?? "Mon compte"}</span>
							</div>
							<Link
								to="/profil"
								className="popover-item"
								onClick={close}
								role="menuitem"
							>
								<AdminIcon n="user" s={15} />
								<span>Mon profil</span>
							</Link>
							<Link
								to="/profil/securite"
								className="popover-item"
								onClick={close}
								role="menuitem"
							>
								<AdminIcon n="lock" s={15} />
								<span>Sécurité</span>
							</Link>
							<Link
								to="/profil/preferences"
								className="popover-item"
								onClick={close}
								role="menuitem"
							>
								<AdminIcon n="gear" s={15} />
								<span>Préférences</span>
							</Link>
							{(isSuperAdmin || has("USERS.READ")) && (
								<Link
									to="/administration"
									className="popover-item"
									onClick={close}
									role="menuitem"
								>
									<AdminIcon n="shield" s={15} />
									<span>Administration</span>
								</Link>
							)}
							<div className="popover-sep" />
							<button
								type="button"
								className="popover-item danger"
								role="menuitem"
								data-testid="topbar-logout"
								onClick={() => {
									close();
									logout();
									navigate("/login");
								}}
							>
								<AdminIcon n="logout" s={15} />
								<span>Déconnexion</span>
							</button>
						</>
					)}
				</Popover>
			</div>
		</header>
	);
}
